import React from 'react';
import { useTranslation } from 'react-i18next';
import ImportModal from '../common/ImportModal';


/**
 * SAMPLE_TEMPLATE_HEADERS
 *
 * Column headers used when generating a CSV template that users can
 * download and fill before importing customers.
 */
const SAMPLE_TEMPLATE_HEADERS = [
  'owner_name',
  'company_name',
  'customer_type',
  'phone_number_1',
  'tally_name',
  'gst_number',
  'contact_person',
  'address_1',
  'address_2',
  'state',
  'city',
  'country',
  'zip_code',
  'delivery_location',
  'email',
  'phone_number_2',
  'credit_period',
  'sister_company',
];

/**
 * CustomerImportModal
 *
 * Modal component that allows users to upload an Excel file to import
 * customers. It validates the file extension, displays basic status and
 * provides a downloadable sample template.
 *
 * Props:
 * - isOpen: whether the modal is visible
 * - onClose: called when modal should close
 * - onImport(file): async handler to perform import; should return { success }
 * - importing: boolean to indicate a running import
 * - importError/importSuccess: status flags to show outcome messages
 */
const CustomerImportModal = ({ isOpen, onClose, onImport, importing = false, importError = null, importSuccess = false }) => {
  const { t } = useTranslation();
  const strings = t('customers.import', { returnObjects: true });

  // Per-column guidance rendered on the template's "Instructions" sheet:
  // which columns are required, and which accept only fixed/enum values.
  const NOTES = t('importTemplate.notes', { returnObjects: true });
  const SAMPLE_TEMPLATE_COLUMN_SPECS = {
    owner_name: { required: true },
    company_name: { required: true },
    customer_type: { required: true, allowed: ['Customer', 'Supplier'], note: NOTES.customerTypeAllowed },
    phone_number_1: { required: true, note: NOTES.phoneTenDigit },
    tally_name: { required: true },
    gst_number: { required: true, note: NOTES.gstin },
    contact_person: { required: true },
    address_1: { required: true },
    address_2: { required: false },
    state: { required: true },
    city: { required: true },
    country: { required: true },
    zip_code: { required: true, note: NOTES.pinCode },
    delivery_location: { required: false },
    email: { required: true, note: NOTES.email },
    phone_number_2: { required: false, note: NOTES.phoneTenDigit },
    credit_period: { required: true, note: NOTES.creditPeriod },
    sister_company: { required: false },
  };

  return (
    <ImportModal
      isOpen={isOpen}
      onClose={onClose}
      onImport={onImport}
      importing={importing}
      importError={importError}
      importSuccess={importSuccess}
      strings={strings}
      templateHeaders={SAMPLE_TEMPLATE_HEADERS}
      templateColumnSpecs={SAMPLE_TEMPLATE_COLUMN_SPECS}
      templateFilename="customer_import_template"
      inputId="customer-import-input"
      skipHeaderValidation={true}
    />
  );
};

export default CustomerImportModal;
